import { useState, useMemo } from "react"
import WireframeLayout, { buildSidebarSections } from "@/components/WireframeLayout"
import { cn, Badge, Card, CardContent } from "../components/primitives"
import { WAREHOUSES, CHANNEL_META, SKU_DATA, generateDailySales, generateForecast } from "../components/data"
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart,
} from "recharts"

interface Props {
  warehouseId: string
  warehouseName: string
  skuId: string
  channels: string[]
}

type TimeRange = "7d" | "30d" | "90d"
const rangeDays = { "7d": 7, "30d": 30, "90d": 90 }

const trendArrow = { growing: "\u2197", slowing: "\u2198", stable: "\u2192" }
const trendColor = { growing: "text-emerald-600", slowing: "text-red-500", stable: "text-slate-400" }

// ─── Chart Tooltip ───────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2.5 text-xs">
      <div className="font-medium text-slate-600 mb-1">{label}</div>
      {payload.map((p: any) => (
        p.dataKey !== "lower" && p.dataKey !== "band" && (
          <div key={p.dataKey} className="flex items-center gap-1.5">
            <span style={{ width: 8, height: 3, borderRadius: 2, background: p.color, display: "inline-block" }} />
            <span className="text-slate-500 capitalize">{p.dataKey}:</span>
            <span className="font-semibold text-slate-800">{p.value?.toLocaleString()}</span>
          </div>
        )
      ))}
    </div>
  )
}

// ─── Time Range Selector ─────────────────────────────────────────────────────

const TimeRangeSelector = ({ value, onChange }: { value: TimeRange; onChange: (v: TimeRange) => void }) => (
  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
    {(["7d", "30d", "90d"] as TimeRange[]).map((r) => (
      <button
        key={r}
        onClick={() => onChange(r)}
        className={cn(
          "px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
          value === r ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50",
        )}
      >
        {r}
      </button>
    ))}
  </div>
)

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SKUDetail({ warehouseId, warehouseName, skuId, channels }: Props) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30d")

  const warehouse = WAREHOUSES.find((w) => w.id === warehouseId)
  const sku = warehouse?.skus.find((s) => s.id === skuId)
  const skuGlobal = SKU_DATA.find((s) => s.id === skuId)

  if (!warehouse || !sku) {
    return (
      <WireframeLayout title="SKU Not Found" titleIcon="📦" sidebarLinks={buildSidebarSections(null, "inventory-data")}>
        <div className="flex items-center justify-center min-h-[400px] text-slate-400">SKU not found in this warehouse</div>
      </WireframeLayout>
    )
  }

  const skuChannels = Object.keys(sku.channelStock)
  const totalStock = Object.values(sku.channelStock as Record<string, number>).reduce((a, b) => a + b, 0)
  const days = rangeDays[timeRange]

  // Combined sales + forecast chart data
  const combinedData = useMemo(() => {
    const histMap: Record<string, number> = {}
    const fcMap: Record<string, { units: number; upper: number; lower: number }> = {}

    skuChannels.forEach((ch) => {
      generateDailySales(skuId, ch, days, sku.demandTrend).forEach((d) => {
        histMap[d.date] = (histMap[d.date] || 0) + d.units
      })
      generateForecast(skuId, ch, days, sku.demandTrend).forEach((d) => {
        if (!fcMap[d.date]) fcMap[d.date] = { units: 0, upper: 0, lower: 0 }
        fcMap[d.date].units += d.units
        fcMap[d.date].upper += d.upper
        fcMap[d.date].lower += d.lower
      })
    })

    const combined: any[] = []
    Object.entries(histMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([date, units]) => combined.push({ date, sales: units }))

    const lastHist = combined[combined.length - 1]
    const fcEntries = Object.entries(fcMap).sort(([a], [b]) => a.localeCompare(b))
    if (lastHist && fcEntries.length) lastHist.forecast = lastHist.sales

    fcEntries.forEach(([date, { units, upper, lower }]) => {
      combined.push({ date, forecast: units, upper, lower, band: upper - lower })
    })
    return combined
  }, [skuId, skuChannels, sku.demandTrend, days])

  // Per-channel sales data
  const channelData = useMemo(() => {
    const dateMap: Record<string, Record<string, number>> = {}
    skuChannels.forEach((ch) => {
      generateDailySales(skuId, ch, days, sku.demandTrend).forEach((d) => {
        if (!dateMap[d.date]) dateMap[d.date] = {}
        dateMap[d.date][ch] = d.units
      })
    })
    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, ...vals }))
  }, [skuId, skuChannels, sku.demandTrend, days])

  // Per-channel forecast data
  const channelForecastData = useMemo(() => {
    const dateMap: Record<string, Record<string, number>> = {}
    skuChannels.forEach((ch) => {
      generateForecast(skuId, ch, days, sku.demandTrend).forEach((d) => {
        if (!dateMap[d.date]) dateMap[d.date] = {}
        dateMap[d.date][ch] = d.units
      })
    })
    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, ...vals }))
  }, [skuId, skuChannels, sku.demandTrend, days])

  const fmtTick = (d: string) => d.split("-").slice(1).join("/")

  return (
    <WireframeLayout
      title={sku.name}
      subtitle={<span className="text-xs text-slate-400">{skuId} &middot; {warehouseName}</span>}
      titleIcon="📦"
      sidebarLinks={buildSidebarSections(null, "inventory-data")}
    >
      <div className="flex flex-col gap-6 max-w-[1100px]">
        {/* Back link */}
        <a
          href={`/wireframes/inventory/warehouse/${warehouseId}`}
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          style={{ textDecoration: "none" }}
        >
          &larr; Back to {warehouseName}
        </a>

        {/* Header + Quick Stats */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <h1 className="text-lg font-bold text-slate-900">{sku.name}</h1>
              <Badge variant={sku.status}>{sku.status}</Badge>
            </div>
            <div className="text-xs text-slate-400">{skuId} &middot; {warehouseName}</div>
          </div>
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <div className="p-4">
              <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Stock</div>
              <div className="text-xl font-bold text-slate-900">{totalStock.toLocaleString()}</div>
              <div className="text-[10px] text-slate-400">units in warehouse</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Velocity</div>
              <div className="text-xl font-bold text-slate-900">{sku.velocity} <span className="text-xs font-normal text-slate-400">units/day</span></div>
              <div className="text-[10px] text-slate-400">avg daily sales</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Days of Supply</div>
              <div className={cn("text-xl font-bold", sku.dos < 14 ? "text-red-600" : sku.dos > 120 ? "text-violet-600" : "text-slate-900")}>{sku.dos}<span className="text-xs font-normal text-slate-400"> days</span></div>
              <div className="text-[10px] text-slate-400">at current velocity</div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1">Demand Trend</div>
              <div className={cn("text-xl font-bold", trendColor[sku.demandTrend])}>
                {trendArrow[sku.demandTrend]} {sku.demandTrend}
              </div>
              <div className="text-[10px] text-slate-400">vs prior period</div>
            </div>
          </Card>
        </div>

        {/* Combined Sales & Forecast Chart */}
        <Card>
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-900">Sales History & Forecast</h2>
              <div className="flex items-center gap-4 text-[10px] text-slate-400">
                <span className="flex items-center gap-1">
                  <span style={{ width: 14, height: 2, background: "#3b82f6", display: "inline-block", borderRadius: 1 }} /> Past sales
                </span>
                <span className="flex items-center gap-1">
                  <span style={{ width: 14, height: 0, borderTop: "2px dashed #3b82f6", display: "inline-block" }} /> Forecast
                </span>
                <span className="flex items-center gap-1">
                  <span style={{ width: 14, height: 6, background: "#3b82f6", opacity: 0.1, display: "inline-block", borderRadius: 1 }} /> Confidence
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={combinedData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtTick} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} width={45} />
                <Tooltip content={<ChartTooltip />} />
                <Area dataKey="lower" stackId="band" fill="transparent" stroke="none" />
                <Area dataKey="band" stackId="band" fill="#3b82f6" fillOpacity={0.08} stroke="none" />
                <Area dataKey="sales" fill="#3b82f6" fillOpacity={0.12} stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls={false} />
                <Line dataKey="forecast" stroke="#3b82f6" strokeDasharray="6 3" strokeWidth={2} dot={false} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Channel Sales + Forecast side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Channel Sales History */}
          <Card>
            <div className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">Channel Sales Breakdown</h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={channelData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={fmtTick} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9 }} width={35} />
                  <Tooltip content={<ChartTooltip />} />
                  {skuChannels.map((ch) => (
                    <Line key={ch} dataKey={ch} stroke={CHANNEL_META[ch]?.color || "#94a3b8"} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {skuChannels.map((ch) => (
                  <span key={ch} className="flex items-center gap-1 text-[10px] text-slate-400">
                    <span style={{ width: 10, height: 2, borderRadius: 1, background: CHANNEL_META[ch]?.color, display: "inline-block" }} />
                    {ch}
                  </span>
                ))}
              </div>
            </div>
          </Card>

          {/* Channel Forecast */}
          <Card>
            <div className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">Channel Forecast</h2>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={channelForecastData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={fmtTick} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 9 }} width={35} />
                  <Tooltip content={<ChartTooltip />} />
                  {skuChannels.map((ch) => (
                    <Line key={ch} dataKey={ch} stroke={CHANNEL_META[ch]?.color || "#94a3b8"} strokeWidth={2} strokeDasharray="6 3" dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {skuChannels.map((ch) => (
                  <span key={ch} className="flex items-center gap-1 text-[10px] text-slate-400">
                    <span style={{ width: 10, height: 0, borderTop: "2px dashed", borderColor: CHANNEL_META[ch]?.color, display: "inline-block" }} />
                    {ch}
                  </span>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Channel Performance Table */}
        <Card>
          <div className="p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">Channel Performance</h2>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Channel</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Stock</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Share</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Restock Days</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Stock Level</th>
                </tr>
              </thead>
              <tbody>
                {skuChannels.map((ch) => {
                  const stock = sku.channelStock[ch]
                  const pct = Math.round((stock / totalStock) * 100)
                  const restock = sku.restockDays?.[ch]
                  const meta = CHANNEL_META[ch] || CHANNEL_META.DTC
                  return (
                    <tr key={ch} className="border-b border-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
                          <span className="text-sm font-medium text-slate-900">{ch}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-semibold text-slate-900">{stock.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-slate-600">{pct}%</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {restock && (
                          <span className={cn("text-sm font-medium", restock < 14 ? "text-red-600" : restock < 30 ? "text-amber-500" : "text-slate-600")}>
                            {restock} days
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-full max-w-[160px]">
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, background: meta.color }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Margin info from global SKU data */}
        {skuGlobal && (
          <Card>
            <div className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">Product Info</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Margin</div>
                  <div className="text-lg font-bold text-slate-900">{(skuGlobal.margin * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">30d Forecast</div>
                  <div className="text-lg font-bold text-slate-900">{skuGlobal.fc.toLocaleString()} units</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Primary Channel</div>
                  <div className="text-lg font-bold text-slate-900">{skuGlobal.ch}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Global Stock</div>
                  <div className="text-lg font-bold text-slate-900">{skuGlobal.stock.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </WireframeLayout>
  )
}
